// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode, Ref } from 'react';

import { Group, Text } from '@mantine/core';

import { SelectValue } from 'components/Mantine/TypeSafeSelect/SelectValue';

/** `Text` defaults a size larger than the input's own text, so inline content opts into the input's. */
const inlineFontSize = { fontSize: 'var(--input-fz)' };

type InlineLabelProps = {
  /** Rendered before the value; the trailing colon is added here, not by the caller. */
  label: ReactNode;
};

const InlineLabel = ({ label }: InlineLabelProps) => (
  <Text span inherit c="dimmed" style={{ whiteSpace: 'nowrap' }}>
    {label}:
  </Text>
);

type InlineLabelValueProps = InlineLabelProps & {
  /** Label of the selected option, or `null` when nothing is selected. */
  selectedLabel: string | null;
  placeholder: string | undefined;
};

/**
 * `Label: value` for the non-searchable variant, whose value is the children of a button and so can
 * hold the label beside it.
 */
export const InlineLabelValue = ({ label, selectedLabel, placeholder }: InlineLabelValueProps) => (
  <Group gap={4} wrap="nowrap" style={inlineFontSize}>
    <InlineLabel label={label} />
    <SelectValue selectedLabel={selectedLabel} placeholder={placeholder} inherit />
  </Group>
);

type InlineLabelSectionProps = InlineLabelProps & {
  /** Rendered before the label, sharing the same section. */
  leftSection: ReactNode;
  ref: Ref<HTMLDivElement>;
};

/**
 * Inline label for the searchable variant, where the value lives in a real `input` the label cannot
 * share. The input's left section is absolutely positioned and sized by `leftSectionWidth`, so this
 * must never shrink to its container — the parent measures it and feeds its width back. The measured
 * width is what the input is padded by, so the trailing padding here is what separates the label
 * from the input's own text.
 */
export const InlineLabelSection = ({ label, leftSection, ref }: InlineLabelSectionProps) => (
  <Group
    ref={ref}
    gap="xs"
    wrap="nowrap"
    ps="var(--input-padding)"
    pe="xs"
    style={{ flex: '0 0 auto', ...inlineFontSize }}
  >
    {leftSection}
    <InlineLabel label={label} />
  </Group>
);
